/**
 * Membership next-charge read (DNKADM-0374, DNKAPI-0407 + DNKAPI-0531 addendum).
 *
 * `GET /v2/admin/members/{contactId}/membership/next-charge` — OWNER/ADMIN only.
 * Tenant derives from the JWT — never send tenant/location/identity as authority.
 * Parsed defensively; all six fields are present on a 2xx. Do NOT cache in
 * localStorage / module TTL. A 403 → throw ApiError as usual (the UI hides the
 * block on any failure — no toast, no retry). Do NOT fold into `members.ts`.
 *
 * **0531 addendum:** a brand-new anchored subscription MAY have a `nextBillingOn`
 * date (its first full-plan charge). Show it — never add copy saying new members
 * never have a date. Unanchored Square new memberships may be `not_available`;
 * that is normal, not an error, not a retry ticket.
 */
import { request } from './client';

const PATH = '/v2/admin/members';

/** Known `reason` discriminants from DNKAPI-0407. Unknown values are normalized to `null`. */
type NextChargeReason =
  | 'not_available'
  | 'canceled'
  | 'cancel_scheduled'
  | 'paused'
  | 'no_membership'
  | null;

/**
 * Parsed `GET /v2/admin/members/{contactId}/membership/next-charge`.
 * All six fields are always present on a 2xx. `amountIsEstimate` is always `true`
 * in practice — the spec documents it but says to ignore a `false` if it ever
 * arrives (always treat as an estimate). `currency` is null exactly when `amount`
 * is; if they disagree, treat `amount` as null (no $0.00 render).
 */
export interface MembershipNextCharge {
  /** Next billing date in `YYYY-MM-DD` local calendar, or `null` when not scheduled. */
  nextBillingOn: string | null;
  /** Amount in major units (e.g. 230.00 = $230), or `null`. */
  amount: number | null;
  /** ISO-4217 currency, `null` exactly when `amount` is `null`. */
  currency: string | null;
  /**
   * Always `true` in practice. An estimate label ("est.") must always accompany
   * the amount; a `false` value on the wire is ignored (treated as `true`).
   */
  amountIsEstimate: true;
  /**
   * Reason the next charge is not a scheduled date, or `null` when there IS a date.
   * Unknown wire values are normalized to `null`.
   */
  reason: NextChargeReason;
  /**
   * When a pause takes effect, in `YYYY-MM-DD` local calendar. Relevant when
   * `reason` is not yet `'paused'` but a pause is already scheduled.
   */
  pauseEffectiveOn: string | null;
}

// --- Defensive parsing helpers ---

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function asText(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
}

function asNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim().length > 0) {
    const n = Number(value);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

const KNOWN_REASONS = new Set([
  'not_available',
  'canceled',
  'cancel_scheduled',
  'paused',
  'no_membership',
]);

function asReason(value: unknown): NextChargeReason {
  const s = asText(value);
  if (s === null) return null;
  return KNOWN_REASONS.has(s) ? (s as NextChargeReason) : null;
}

function parse(body: unknown): MembershipNextCharge {
  const o = isObject(body) ? body : {};
  const rawAmount = asNumber(o['amount']);
  const rawCurrency = asText(o['currency']);
  // If amount/currency disagree on null-ness, treat amount as null (spec).
  const amount = rawAmount !== null && rawCurrency !== null ? rawAmount : null;
  const currency = amount !== null ? rawCurrency : null;
  return {
    nextBillingOn: asText(o['nextBillingOn']),
    amount,
    currency,
    amountIsEstimate: true, // always; ignore a false per spec
    reason: asReason(o['reason']),
    pauseEffectiveOn: asText(o['pauseEffectiveOn']),
  };
}

/**
 * `GET /v2/admin/members/{contactId}/membership/next-charge` → the member's next
 * scheduled charge. Throws `ApiError` on any non-2xx (including `403`/`404`).
 * The caller hides the billing block on any failure; never toasts.
 * OWNER/ADMIN only — STAFF should not mount the surface that calls this.
 */
export async function getMembershipNextCharge(
  contactId: string,
): Promise<MembershipNextCharge> {
  const path = `${PATH}/${encodeURIComponent(contactId)}/membership/next-charge`;
  const body = await request<unknown>('GET', path);
  return parse(body);
}
